import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FaClipboardList, FaHistory, FaHourglassHalf } from 'react-icons/fa';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ReviewPopup from '../../Components/buyer/ReviewPopup';
import ReviewHistoryContainer from '../../Components/buyer/ReviewHistoryContainer';
import Loader from '../../Components/utils/Loader';

const MyReviews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSellerPanel = location.pathname.startsWith('/seller/myReviews');

  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/seller/myReviews')) {
      return new URLSearchParams(window.location.search).get('tab') === 'history' ? 'sellerHistory' : 'awaitingBuyerReview';
    }
    return 'toBeReviewed';
  });

  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [historyReviews, setHistoryReviews] = useState([]);
  const [isLoadingToBeReviewed, setIsLoadingToBeReviewed] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historySort, setHistorySort] = useState('newest');
  const [historyRatingFilter, setHistoryRatingFilter] = useState('all');

  useEffect(() => {
    if (!isSellerPanel) return;
    const t = searchParams.get('tab');
    if (t === 'history') setActiveTab('sellerHistory');
    else setActiveTab('awaitingBuyerReview');
  }, [isSellerPanel, searchParams]);

  useEffect(() => {
    if (!isSellerPanel) setActiveTab('toBeReviewed');
  }, [isSellerPanel]);

  useEffect(() => {
    const fetchToBeReviewed = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/reviews/to-be-reviewed`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setReviews(response.data);
      } catch (error) {
        console.error('Error fetching to-be-reviewed orders', error);
      } finally {
        setIsLoadingToBeReviewed(false);
      }
    };

    if (!isSellerPanel && activeTab === 'toBeReviewed') {
      setIsLoadingToBeReviewed(true);
      fetchToBeReviewed();
    }
  }, [activeTab, isSellerPanel]);

  useEffect(() => {
    const fetchSellerAwaiting = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/reviews/seller/awaiting-buyer-review`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setReviews(response.data);
      } catch (error) {
        console.error('Error fetching seller awaiting-review orders', error);
      } finally {
        setIsLoadingToBeReviewed(false);
      }
    };

    if (isSellerPanel && activeTab === 'awaitingBuyerReview') {
      setIsLoadingToBeReviewed(true);
      fetchSellerAwaiting();
    }
  }, [activeTab, isSellerPanel]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/reviews/history`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setHistoryReviews(response.data);
      } catch (error) {
        console.error('Error fetching review history', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    if (!isSellerPanel && activeTab === 'history') {
      setIsLoadingHistory(true);
      fetchHistory();
    }
  }, [activeTab, isSellerPanel]);

  useEffect(() => {
    const fetchSellerHistory = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/reviews/seller/history`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setHistoryReviews(response.data);
      } catch (error) {
        console.error('Error fetching seller review history', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    if (isSellerPanel && activeTab === 'sellerHistory') {
      setIsLoadingHistory(true);
      fetchSellerHistory();
    }
  }, [activeTab, isSellerPanel]);

  const handleReviewClick = (review) => {
    setSelectedReview(review);
    setShowReviewPopup(true);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
    setSelectedReview(null);
  };

  const handleSubmitReview = async (reviewData) => {
    try {
      const formData = new FormData();
      formData.append('orderId', selectedReview.customOrderId);
      formData.append('ratings', JSON.stringify(reviewData.ratings));
      formData.append('comment', reviewData.comment);
      reviewData.attachedImages.forEach((image) => {
        formData.append('images', image);
      });

      const response = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/reviews`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newReview = response.data.review;
      setReviews(reviews.filter(r => r.id !== selectedReview.id));
      setHistoryReviews([newReview, ...historyReviews]);
    } catch (error) {
      console.error('Error submitting review', error);
      throw error;
    }
  };

  const goToSellerTab = (tab) => {
    if (tab === 'sellerHistory') {
      setActiveTab('sellerHistory');
      setSearchParams({ tab: 'history' }, { replace: true });
    } else {
      setActiveTab('awaitingBuyerReview');
      setSearchParams({ tab: 'awaiting' }, { replace: true });
    }
  };

  const filteredHistoryReviews = useMemo(() => {
    let list = [...historyReviews];
    if (historyRatingFilter === '5') list = list.filter((r) => r.overallRating >= 4.5);
    else if (historyRatingFilter === '4') list = list.filter((r) => r.overallRating >= 4);
    else if (historyRatingFilter === '3') list = list.filter((r) => r.overallRating >= 3);
    else if (historyRatingFilter === 'low') list = list.filter((r) => r.overallRating < 3);
    list.sort((a, b) => {
      const da = new Date(a.createdAt || 0).getTime();
      const db = new Date(b.createdAt || 0).getTime();
      return historySort === 'newest' ? db - da : da - db;
    });
    return list;
  }, [historyReviews, historySort, historyRatingFilter]);

  const pendingCount = useMemo(() => {
    if (isSellerPanel) {
      return activeTab === 'awaitingBuyerReview' && !isLoadingToBeReviewed ? reviews.length : null;
    }
    return activeTab === 'toBeReviewed' && !isLoadingToBeReviewed ? reviews.length : null;
  }, [isSellerPanel, activeTab, isLoadingToBeReviewed, reviews.length]);

  const pageTitle = isSellerPanel ? 'Ratings & feedback' : 'My reviews';
  const pageSubtitle = isSellerPanel
    ? 'Feedback buyers left on your orders appears here. Completed orders that don’t have a rating yet are listed so you can follow up with the buyer if needed.'
    : 'Share feedback with sellers after your orders are complete, and revisit ratings you\'ve already posted.';

  const showBuyerToReview = !isSellerPanel && activeTab === 'toBeReviewed';
  const showSellerAwaiting = isSellerPanel && activeTab === 'awaitingBuyerReview';
  const isHistoryTab =
    (!isSellerPanel && activeTab === 'history') ||
    (isSellerPanel && activeTab === 'sellerHistory');

  const clearHistoryFilters = () => {
    setHistorySort('newest');
    setHistoryRatingFilter('all');
  };

  return (
    <div className="myReviewsPageWrap">
      <section className="mainSection myReviewsInner">
        <header className="myReviewsPageHeader">
          <h1 className="pageMainHeading myReviewsPageTitle">{pageTitle}</h1>
          <p className="myReviewsPageSubtitle">
            {pageSubtitle}
          </p>
        </header>

        <div className="myReviewsTabStrip" role="tablist" aria-label={isSellerPanel ? 'Seller feedback sections' : 'Review sections'}>
          {!isSellerPanel ? (
            <>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'toBeReviewed'}
                className={`myReviewsTab ${activeTab === 'toBeReviewed' ? 'myReviewsTab--active' : ''}`}
                onClick={() => setActiveTab('toBeReviewed')}
              >
                <FaClipboardList className="myReviewsTabIcon" aria-hidden />
                <span className="myReviewsTabLabel">To review</span>
                {pendingCount !== null && pendingCount > 0 && (
                  <span className="myReviewsTabBadge">{pendingCount > 99 ? '99+' : pendingCount}</span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'history'}
                className={`myReviewsTab ${activeTab === 'history' ? 'myReviewsTab--active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                <FaHistory className="myReviewsTabIcon" aria-hidden />
                <span className="myReviewsTabLabel">History</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'awaitingBuyerReview'}
                className={`myReviewsTab ${activeTab === 'awaitingBuyerReview' ? 'myReviewsTab--active' : ''}`}
                onClick={() => goToSellerTab('awaitingBuyerReview')}
              >
                <FaHourglassHalf className="myReviewsTabIcon" aria-hidden />
                <span className="myReviewsTabLabel">Awaiting buyer review</span>
                {pendingCount !== null && pendingCount > 0 && (
                  <span className="myReviewsTabBadge">{pendingCount > 99 ? '99+' : pendingCount}</span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'sellerHistory'}
                className={`myReviewsTab ${activeTab === 'sellerHistory' ? 'myReviewsTab--active' : ''}`}
                onClick={() => goToSellerTab('sellerHistory')}
              >
                <FaHistory className="myReviewsTabIcon" aria-hidden />
                <span className="myReviewsTabLabel">History</span>
              </button>
            </>
          )}
        </div>

        <div className="myReviewsPanel">
          {(showBuyerToReview || showSellerAwaiting) ? (
            isLoadingToBeReviewed ? (
              <div className="myReviewsLoaderWrap">
                <Loader type="simpleMini" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="myReviewsEmpty">
                <div className="myReviewsEmptyIcon" aria-hidden>
                  {isSellerPanel ? <FaHourglassHalf /> : <FaClipboardList />}
                </div>
                <h2 className="myReviewsEmptyTitle">{isSellerPanel ? 'No orders waiting for buyer reviews' : 'You\'re all caught up'}</h2>
                <p className="myReviewsEmptyText">
                  {isSellerPanel
                    ? 'When a completed order has not been rated yet, it will show here so you can message the buyer if you want to follow up.'
                    : 'Completed orders that need a rating will show up here. Finish an order, then leave a review to help other buyers.'}
                </p>
              </div>
            ) : (
              <ul className="myReviewsToReviewList">
                {reviews.map((review) => (
                  <li key={review.id} className="myReviewsToReviewCard">
                    <div className="myReviewsToReviewMain">
                      <div className="myReviewsToReviewThumb">
                        <img src={review.image} alt="" className="myReviewsToReviewImg" />
                      </div>
                      <div className="myReviewsToReviewBody">
                        <h3 className="myReviewsToReviewTitle">{review.title}</h3>
                        <p className="myReviewsToReviewDesc">{review.description}</p>
                      </div>
                    </div>
                    {isSellerPanel ? (
                      <button
                        type="button"
                        className="primaryBtn2 myReviewsToReviewBtn"
                        disabled={!review.buyerId}
                        onClick={() => review.buyerId && navigate(`/seller/chats?p=${review.buyerId}`)}
                      >
                        Contact buyer
                      </button>
                    ) : (
                      <button type="button" className="primaryBtn2 myReviewsToReviewBtn" onClick={() => handleReviewClick(review)}>
                        Write review
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )
          ) : (
            isLoadingHistory ? (
              <div className="myReviewsLoaderWrap">
                <Loader type="simpleMini" />
              </div>
            ) : historyReviews.length === 0 ? (
              <div className="myReviewsEmpty">
                <div className="myReviewsEmptyIcon" aria-hidden>
                  <FaHistory />
                </div>
                <h2 className="myReviewsEmptyTitle">No reviews yet</h2>
                <p className="myReviewsEmptyText">
                  {isSellerPanel
                    ? 'When a buyer rates your product or service, their feedback will appear here.'
                    : 'When you rate a seller, your feedback will appear here with the details you shared.'}
                </p>
              </div>
            ) : filteredHistoryReviews.length === 0 ? (
              <div className="myReviewsEmpty">
                <h2 className="myReviewsEmptyTitle">No matching reviews</h2>
                <p className="myReviewsEmptyText">Try changing sort or rating filters.</p>
                <button type="button" className="primaryBtn2 myReviewsToReviewBtn" onClick={clearHistoryFilters}>
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="myReviewsHistoryScroll">
                {isHistoryTab && historyReviews.length > 0 && (
                  <div className="myReviewsHistoryToolbar" role="group" aria-label="Filter and sort history">
                    <div className="myReviewsHistoryToolbarField">
                      <label htmlFor="myReviewsSort">Sort by date</label>
                      <select
                        id="myReviewsSort"
                        className="myReviewsHistorySelect"
                        value={historySort}
                        onChange={(e) => setHistorySort(e.target.value)}
                      >
                        <option value="newest">Newest first</option>
                        <option value="oldest">Oldest first</option>
                      </select>
                    </div>
                    <div className="myReviewsHistoryToolbarField">
                      <label htmlFor="myReviewsRating">Rating</label>
                      <select
                        id="myReviewsRating"
                        className="myReviewsHistorySelect"
                        value={historyRatingFilter}
                        onChange={(e) => setHistoryRatingFilter(e.target.value)}
                      >
                        <option value="all">All ratings</option>
                        <option value="5">4.5+ (top ratings)</option>
                        <option value="4">4 & up</option>
                        <option value="3">3 & up</option>
                        <option value="low">Below 3</option>
                      </select>
                    </div>
                  </div>
                )}
                <ReviewHistoryContainer
                  reviews={filteredHistoryReviews}
                  isMyReview={!isSellerPanel}
                  isProductReview={isSellerPanel}
                  showRatingQuestions={!isSellerPanel}
                  sellerReceivedReview={isSellerPanel}
                />
              </div>
            )
          )}
        </div>
      </section>
      {showReviewPopup && selectedReview && (
        <ReviewPopup
          review={selectedReview}
          onClose={handleClosePopup}
          onSubmit={handleSubmitReview}
        />
      )}
    </div>
  );
};

export default MyReviews;
